import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_BASE || '';

const StatusBadge = ({ status }) => {
  const s = (status || '').toLowerCase();
  if (s === 'enviado') return <span className="badge bg-success"><i className="fas fa-check me-1"></i>Enviado</span>;
  if (s === 'erro') return <span className="badge bg-danger"><i className="fas fa-exclamation-triangle me-1"></i>Erro</span>;
  return <span className="badge bg-secondary">{status || '—'}</span>;
};

export default function HistoricoEnvioLucrosSocios() {
  const [historico, setHistorico] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [filtros, setFiltros] = useState({ busca: '', mesReferencia: '', status: '' });

  useEffect(() => {
    const token = localStorage.getItem('access');
    setCarregando(true);
    axios.get(`${API}/api/historico-envio-lucros-socios/`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => { setHistorico(r.data.historico || []); setErro(null); })
      .catch(() => setErro('Não foi possível carregar o histórico.'))
      .finally(() => setCarregando(false));
  }, []);

  const mesesReferencia = useMemo(
    () => [...new Set(historico.map(h => h.mes_referencia))].filter(Boolean).sort().reverse(),
    [historico]
  );

  const filtrados = useMemo(() => {
    const b = filtros.busca.toLowerCase();
    return historico.filter(h => {
      if (b && !h.cliente?.toLowerCase().includes(b) &&
          !(h.numero_dominio || '').toString().includes(b) &&
          !h.nome_socio?.toLowerCase().includes(b)) return false;
      if (filtros.mesReferencia && h.mes_referencia !== filtros.mesReferencia) return false;
      if (filtros.status && h.status_envio !== filtros.status) return false;
      return true;
    });
  }, [historico, filtros]);

  if (carregando) {
    return (
      <div className="das-page">
        <div className="container-fluid mt-5 text-center">
          <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }}></div>
          <p className="mt-3 text-muted">Carregando histórico de envios...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="das-page" style={{ zoom: 1.1 }}>
      <div className="container-fluid mt-4">

        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center mb-2">
                      <i className="fas fa-envelope-open-text header-icon me-2"></i>
                      <h2 className="titulo-pagina m-0">Envio de Lucros aos Sócios</h2>
                    </div>
                    <p className="mb-1 opacity-75">
                      <i className="fas fa-database me-2"></i>
                      Histórico dos e-mails mensais de confirmação de distribuição de lucros/dividendos
                    </p>
                  </div>
                  <div className="mb-2">
                    <button
                      className="btn btn-sm"
                      onClick={() => window.history.back()}
                      style={{ backgroundColor: '#00205b', borderColor: '#00205b', color: '#fff' }}
                    >
                      <i className="fas fa-arrow-left me-1"></i>Voltar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {erro && (
          <div className="alert alert-danger alert-dismissible mb-4">
            <i className="fas fa-exclamation-circle me-2"></i>{erro}
            <button className="btn-close" onClick={() => setErro(null)}></button>
          </div>
        )}

        {/* Filtros */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-white">
            <h6 className="mb-0">
              <i className="fas fa-filter me-2 text-primary"></i>Filtros
            </h6>
          </div>
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-md-5">
                <label className="form-label fw-semibold small">Buscar cliente / Nº domínio / sócio</label>
                <input type="text" className="form-control form-control-sm"
                  placeholder="Digite o nome ou número..."
                  value={filtros.busca}
                  onChange={e => setFiltros(f => ({ ...f, busca: e.target.value }))} />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold small">Mês de Referência</label>
                <select className="form-select form-select-sm" value={filtros.mesReferencia}
                  onChange={e => setFiltros(f => ({ ...f, mesReferencia: e.target.value }))}>
                  <option value="">Todos</option>
                  {mesesReferencia.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label fw-semibold small">Status</label>
                <select className="form-select form-select-sm" value={filtros.status}
                  onChange={e => setFiltros(f => ({ ...f, status: e.target.value }))}>
                  <option value="">Todos</option>
                  <option value="enviado">Enviado</option>
                  <option value="erro">Erro</option>
                </select>
              </div>
              <div className="col-md-2">
                {(filtros.busca || filtros.mesReferencia || filtros.status) && (
                  <button className="btn btn-sm btn-outline-secondary w-100"
                    onClick={() => setFiltros({ busca: '', mesReferencia: '', status: '' })}>
                    <i className="fas fa-times me-1"></i>Limpar
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tabela */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-white d-flex justify-content-between align-items-center">
            <h6 className="mb-0">
              <i className="fas fa-list me-2 text-secondary"></i>
              Envios ({filtrados.length})
            </h6>
          </div>
          <div className="card-body p-0">
            {filtrados.length === 0 ? (
              <div className="text-center py-5">
                <i className="fas fa-inbox fa-3x text-muted mb-3 d-block" style={{ opacity: 0.3 }}></i>
                <h5 className="text-muted">Nenhum envio encontrado</h5>
                <p className="text-muted small mb-0">
                  {historico.length > 0
                    ? 'Tente ajustar os filtros.'
                    : 'Os envios feitos pelo script aparecerão aqui.'}
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Data Envio</th>
                      <th>Cliente</th>
                      <th>Nº Domínio</th>
                      <th>Sócio</th>
                      <th>E-mail</th>
                      <th>Mês Ref.</th>
                      <th className="text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtrados.map(h => (
                      <tr key={h.id}>
                        <td className="text-nowrap" style={{ fontSize: '0.83rem' }}>{h.data_envio}</td>
                        <td className="fw-semibold" style={{ fontSize: '0.85rem' }}>{h.cliente || '—'}</td>
                        <td><code style={{ fontSize: '0.80rem' }}>{h.numero_dominio || '—'}</code></td>
                        <td style={{ fontSize: '0.83rem' }}>{h.nome_socio || '—'}</td>
                        <td style={{ fontSize: '0.83rem' }}>{h.email_socio || '—'}</td>
                        <td style={{ fontSize: '0.83rem' }}>{h.mes_referencia || '—'}</td>
                        <td className="text-center">
                          <StatusBadge status={h.status_envio} />
                          {h.status_envio === 'erro' && h.erro_detalhe && (
                            <i className="fas fa-info-circle ms-2 text-danger" style={{ cursor: 'help' }}
                              title={h.erro_detalhe}></i>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
